import React from 'react';
import { motion, MotionConfig } from 'framer-motion';
import { cn } from '@/lib/utils';
/**
 * State-driven segmented control with the same visual treatment as the
 * router-coupled {@link SegmentedTabs}: rounded-full pill container and
 * an animated active-segment indicator that slides between segments via
 * framer-motion's FLIP.
 *
 * Use this whenever the tab is purely UI state — e.g. Theme vs.
 * Background inside the campaign Customize panel. For URL-driven tabs
 * (analytics pages, A/B testing sub-tabs) keep using `SegmentedTabs`.
 */
export function SegmentedToggle({ value, onValueChange, items, className, layoutId, disabled, }) {
    const id = React.useId();
    const resolvedLayoutId = layoutId ?? `segmented-toggle-${id}`;
    return (<MotionConfig transition={{ type: 'spring', bounce: 0.18, duration: 0.45 }}>
            <div role="tablist" aria-disabled={disabled || undefined} className={cn('relative inline-flex items-center gap-0.5 rounded-full border bg-muted/60 p-1 text-sm shadow-inner dark:bg-muted/30', disabled && 'opacity-60 pointer-events-none', className)}>
                {items.map((item) => {
            const matched = item.value === value;
            const segmentBase = 'relative inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-sm font-medium transition-colors';
            if (item.disabled) {
                return (<span key={item.value} role="tab" aria-selected={matched} aria-disabled="true" className={cn(segmentBase, 'cursor-not-allowed text-muted-foreground/60 opacity-70')}>
                                <span className="relative z-10 inline-flex items-center gap-1.5">
                                    {item.icon}
                                    {item.label}
                                </span>
                            </span>);
            }
            return (<button key={item.value} type="button" role="tab" aria-selected={matched} onClick={() => onValueChange(item.value)} className={cn(segmentBase, matched ? 'text-primary-foreground' : 'text-muted-foreground hover:text-foreground')}>
                            {matched && (<motion.span layoutId={resolvedLayoutId} className="absolute inset-0 rounded-full bg-primary shadow-sm" aria-hidden="true"/>)}
                            <span className="relative z-10 inline-flex items-center gap-1.5">
                                {item.icon}
                                {item.label}
                            </span>
                        </button>);
        })}
            </div>
        </MotionConfig>);
}
